import {useState, useEffect, useMemo} from 'react';
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import CustomersTable from '../components/customersTable.js';
import DeleteModal from '../modals/delete.js';
import ConfirmationModal from '../modals/confirmation.js';
import SendOnboardingInviteForm from '../forms/sendOnboardingInvite.js';
import SlideOverRight from '../components/slideOverRight.js';
import getOnboardingInvites from '../functions/API/getOnboardingInvites.js';
import postResendOnboardingInvite from '../functions/API/postResendOnboardingInvite.js';
import postCancelOnboardingInvite from '../functions/API/postCancelOnboardingInvite.js';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';

export default function Customers() {

    const [activeTab, setActiveTab] = useState('customers');
    const [deleteModal, setDeleteModal] = useState(false);
    const [IDToDelete, setIDToDelete] = useState();
    const [shouldRefresh, setShouldRefresh] = useState(false);

    // Invite state
    const [inviteForm, setInviteForm] = useState(false);
    const [invites, setInvites] = useState([]);
    const [inviteRefresh, setInviteRefresh] = useState(0);
    const [inviteLoading, setInviteLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [inviteSearchQuery, setInviteSearchQuery] = useState('');
    const [inviteStatusFilter, setInviteStatusFilter] = useState('all');
    const [cancelModal, setCancelModal] = useState(false);
    const [inviteToCancel, setInviteToCancel] = useState(null);

    function showDeleteModal(id){
        setIDToDelete(id);
        setDeleteModal(true);
    }

    function refresh(){
        setShouldRefresh(!shouldRefresh);
    }

    // Invite functions
    async function fetchInvites() {
        const invitesRes = await getOnboardingInvites();
        if (invitesRes.response.ok && invitesRes.data?.invites) {
            setInvites(invitesRes.data.invites);
        }
        setInviteLoading(false);
    }

    useEffect(() => {
        fetchInvites();
    }, [inviteRefresh]);

    const filteredInvites = useMemo(() => {
        return invites.filter(invite => {
            if (inviteStatusFilter !== 'all' && invite.status !== inviteStatusFilter) return false;
            if (inviteSearchQuery) {
                const q = inviteSearchQuery.toLowerCase();
                const name = `${invite.first_name} ${invite.last_name}`.toLowerCase();
                const email = invite.email.toLowerCase();
                if (!name.includes(q) && !email.includes(q)) return false;
            }
            return true;
        });
    }, [invites, inviteStatusFilter, inviteSearchQuery]);

    async function handleResend(inviteId) {
        setActionLoading({...actionLoading, [inviteId]: true});
        const response = await postResendOnboardingInvite({ invite_id: inviteId });
        if (response.response.ok) {
            toast.success('Invite resent successfully.');
            setInviteRefresh(inviteRefresh + 1);
        } else {
            toast.error(response.data?.message || 'Failed to resend invite.');
        }
        setActionLoading({...actionLoading, [inviteId]: false});
    }

    async function handleCancel(inviteId) {
        setActionLoading({...actionLoading, [inviteId]: true});
        const response = await postCancelOnboardingInvite({ invite_id: inviteId });
        if (response.response.ok) {
            toast.success('Invite cancelled.');
            setInviteRefresh(inviteRefresh + 1);
        } else {
            toast.error(response.data?.message || 'Failed to cancel invite.');
        }
        setActionLoading({...actionLoading, [inviteId]: false});
    }

    function getStatusTag(status) {
        switch(status) {
            case 'pending':
                return <span className="inline-flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900/30 px-2 py-1 text-xs font-medium text-yellow-700 dark:text-yellow-400 ring-1 ring-inset ring-yellow-600/20">Pending</span>;
            case 'used':
                return <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/20">Used</span>;
            case 'expired':
                return <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-zinc-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-600/20">Expired</span>;
            case 'cancelled':
                return <span className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-400 ring-1 ring-inset ring-red-600/20">Cancelled</span>;
            default:
                return <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-zinc-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-600/20">{status}</span>;
        }
    }

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    }

    const tabs = [
        { key: 'customers', label: 'Customers' },
        { key: 'invites', label: 'Onboarding Invites' },
    ];

    return (
        <div>
            <AdminSidebar>
                <div className="flex items-center justify-between mb-6">
                    <nav className="flex space-x-4" aria-label="Tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    activeTab === tab.key
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                    {activeTab === 'invites' && (
                        <button onClick={() => setInviteForm(true)} type="button" className="block rounded-md bg-blue-700 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
                            Invite User
                        </button>
                    )}
                </div>

                {activeTab === 'customers' && (
                    <CustomersTable deleteFunction={showDeleteModal} shouldRefresh={shouldRefresh}/>
                )}

                {activeTab === 'invites' && (
                    <div>
                        <div className="sm:flex sm:items-center">
                            <div className="sm:flex-auto">
                                <h1 className="text-2xl text-left leading-6 text-gray-900 dark:text-white">Onboarding Invites</h1>
                                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Manage invites sent to prospective tenants</p>
                            </div>
                        </div>

                        <div className="relative mt-6 flex items-center gap-3">
                            <div className="relative flex-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={inviteSearchQuery}
                                    onChange={(e) => setInviteSearchQuery(e.target.value)}
                                    className="block w-full rounded-md border-0 bg-white dark:bg-zinc-800 py-1.5 pl-10 pr-3 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                />
                            </div>
                            <select
                                value={inviteStatusFilter}
                                onChange={(e) => setInviteStatusFilter(e.target.value)}
                                className="rounded-md border-0 bg-white dark:bg-zinc-800 py-1.5 pl-3 pr-8 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="used">Used</option>
                                <option value="expired">Expired</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>

                        {inviteLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                            </div>
                        ) : (
                            <div className="flow-root">
                                <div className="overflow-x-auto">
                                    <div className="inline-block min-w-full py-2 align-middle">
                                        <table className="min-w-full divide-y divide-gray-300 dark:divide-zinc-700">
                                            <thead>
                                                <tr>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">Name</th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">Email</th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">Status</th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">Sent</th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">Expires</th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0"></th>
                                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 dark:divide-zinc-700 bg-white dark:bg-transparent">
                                                {filteredInvites.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                                            {invites.length === 0 ? 'No invites sent yet.' : 'No invites match your filters.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredInvites.map(invite => (
                                                        <tr key={invite.id}>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                <div className="text-gray-900 dark:text-white">{invite.first_name} {invite.last_name}</div>
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                <div className="text-gray-900 dark:text-white">{invite.email}</div>
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                {getStatusTag(invite.status)}
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                {formatDate(invite.created_at)}
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                {formatDate(invite.expires_at)}
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                {(invite.status === 'pending' || invite.status === 'expired') && (
                                                                    <div onClick={() => handleResend(invite.id)} className="cursor-pointer text-blue-700 hover:text-blue-900 inline">
                                                                        Resend
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="whitespace-nowrap py-3 text-sm text-gray-500 dark:text-gray-400">
                                                                {invite.status === 'pending' && (
                                                                    <button onClick={() => { setInviteToCancel(invite.id); setCancelModal(true); }} disabled={actionLoading[invite.id]} type="button" className="cursor-pointer rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
                                                                        Cancel
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <SlideOverRight visible={inviteForm} setVisible={setInviteForm}>
                    <SendOnboardingInviteForm
                        setVisible={setInviteForm}
                        refresh={setInviteRefresh}
                        refreshValue={inviteRefresh}
                    />
                </SlideOverRight>
            </AdminSidebar>
            <DeleteModal open={deleteModal} setOpen={setDeleteModal} IDToDelete={IDToDelete} refresh={refresh} deleteAction="deleteCustomer" title="Delete customer" message="Are you sure you want to delete this customer? This action cannot be undone." />
            <ConfirmationModal open={cancelModal} setOpen={setCancelModal} title="Cancel invite" message="Are you sure you want to cancel this invite? The recipient will no longer be able to use the invite link." confirmText="Cancel Invite" variant="danger" onConfirm={() => handleCancel(inviteToCancel)} />
        </div>
    );

}
